import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, MailQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSupabaseClient } from "@/services/supabase";

const GENERIC_SUCCESS_MESSAGE =
  "If an account exists for that email, a password recovery link has been sent.";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLocalError(null);

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      // Supabase handles password reset email; the redirect URL tells Supabase
      // where to send the user after they click the recovery link.
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      // Always show the generic success message — never reveal whether
      // the email exists in the system (privacy-safe / anti-enumeration).
      setDone(true);
    } catch {
      // Even on error, show the generic message to prevent enumeration.
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <Card className="border-border/80 shadow-soft" data-testid="forgot-password-success-card">
            <CardHeader className="pb-4">
              <div className="mb-2 flex justify-center">
                <CheckCircle2 className="size-12 text-primary" data-testid="forgot-password-success-icon" />
              </div>
              <CardTitle className="text-center text-lg">Check your email</CardTitle>
              <CardDescription className="text-center">
                {GENERIC_SUCCESS_MESSAGE}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" variant="outline" onClick={() => navigate("/login")} data-testid="forgot-password-back-to-login">
                <ArrowLeft className="mr-2 size-4" />
                Back to login
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <p className="text-sm font-semibold text-primary" data-testid="forgot-password-eyebrow">ACZON Farms</p>
          <h2 className="mt-1 text-3xl font-semibold tracking-tight" data-testid="forgot-password-title">Forgot your password?</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground" data-testid="forgot-password-description">
            Enter your work email and we'll send you a secure link to reset your password.
          </p>
        </div>

        <Card className="border-border/80 shadow-soft" data-testid="forgot-password-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Password recovery</CardTitle>
            <CardDescription>Enter your registered email address</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                  autoComplete="email"
                  placeholder="you@aczonfarms.com"
                  data-testid="forgot-password-email-input"
                />
              </div>
              {localError && (
                <p className="text-sm font-medium text-destructive" data-testid="forgot-password-error-message" role="alert">
                  {localError}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting || !email} data-testid="forgot-password-submit-button">
                {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <MailQuestion className="mr-2 size-4" />}
                {submitting ? "Sending..." : "Send recovery link"}
              </Button>
            </form>
            <div className="mt-5 text-center">
              <Link to="/login" className="inline-flex items-center text-sm font-medium text-primary hover:underline" data-testid="forgot-password-back-link">
                <ArrowLeft className="mr-1 size-3.5" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
